import { useCallback, useEffect, useSyncExternalStore } from "react";

import API_BASE, { protectedApiFetch } from "../api";

const inFlightPostIds = new Set();
const errorsByPostId = new Map();
const errorClearTimersByPostId = new Map();
const listeners = new Set();

export const ORDINARY_POST_LIKE_TIMEOUT_MS = 30000;
export const ORDINARY_POST_LIKE_ERROR_DURATION_MS = 6000;

let revision = 0;
let nextMutationVersion = 0;
let lastMutationTimestamp = 0;

function emitCoordinatorChange() {
  revision += 1;
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return revision;
}

function clearPostLikeError(postId, { notify = true } = {}) {
  const normalizedPostId = String(postId || "");
  const clearTimer = errorClearTimersByPostId.get(normalizedPostId);
  if (clearTimer != null) {
    window.clearTimeout(clearTimer);
    errorClearTimersByPostId.delete(normalizedPostId);
  }

  const deleted = errorsByPostId.delete(normalizedPostId);
  if (deleted && notify) emitCoordinatorChange();
}

function setPostLikeError(postId, message) {
  const normalizedPostId = String(postId || "");
  clearPostLikeError(normalizedPostId, { notify: false });
  errorsByPostId.set(normalizedPostId, message);
  errorClearTimersByPostId.set(
    normalizedPostId,
    window.setTimeout(() => {
      clearPostLikeError(normalizedPostId);
    }, ORDINARY_POST_LIKE_ERROR_DURATION_MS)
  );
  emitCoordinatorChange();
}

function getLatestPostLikeError() {
  const entries = Array.from(errorsByPostId.entries());
  if (!entries.length) return null;
  const [postId, message] = entries[entries.length - 1];
  return { postId, message };
}

export function clearAllOrdinaryPostLikeErrors() {
  const hadErrors = errorsByPostId.size > 0;
  errorClearTimersByPostId.forEach((timerId) => window.clearTimeout(timerId));
  errorClearTimersByPostId.clear();
  errorsByPostId.clear();
  if (hadErrors) emitCoordinatorChange();
}

export function useClearOrdinaryPostLikeErrorsOnUserChange(userId) {
  const normalizedUserId = String(userId || "");

  useEffect(() => {
    clearAllOrdinaryPostLikeErrors();
    return clearAllOrdinaryPostLikeErrors;
  }, [normalizedUserId]);
}

function nextTimestamp() {
  lastMutationTimestamp = Math.max(Date.now(), lastMutationTimestamp + 1);
  return lastMutationTimestamp;
}

function dispatchPostLikeUpdate({
  postId,
  liked,
  likesCount,
  actorUserId,
  mutationPhase,
  mutationVersion,
}) {
  window.dispatchEvent(
    new CustomEvent("postLiked", {
      detail: {
        postId,
        liked,
        likesCount: Math.max(0, Number(likesCount) || 0),
        actorUserId,
        mutationPhase,
        mutationVersion,
        mutationUpdatedAt: nextTimestamp(),
      },
    })
  );
}

export function getOrdinaryPostLikeState(post, actorUserId) {
  const likes = Array.isArray(post?.likes) ? post.likes : [];
  const normalizedActorUserId = String(actorUserId || "");
  const liked = Boolean(
    normalizedActorUserId &&
      likes.some((likeUserId) => String(likeUserId) === normalizedActorUserId)
  );

  return {
    liked,
    likesCount: likes.length,
  };
}

export async function mutateOrdinaryPostLike({
  post,
  actorUserId,
  errorMessage,
  onSuccess,
}) {
  const postId = String(post?.id || "").trim();
  const normalizedActorUserId = String(actorUserId || "").trim();
  if (!postId || !normalizedActorUserId || inFlightPostIds.has(postId)) {
    return false;
  }

  const previousState = getOrdinaryPostLikeState(post, normalizedActorUserId);
  const optimisticState = {
    liked: !previousState.liked,
    likesCount: Math.max(
      0,
      previousState.likesCount + (previousState.liked ? -1 : 1)
    ),
  };
  const mutationVersion = ++nextMutationVersion;

  inFlightPostIds.add(postId);
  clearPostLikeError(postId, { notify: false });
  emitCoordinatorChange();

  dispatchPostLikeUpdate({
    postId,
    ...optimisticState,
    actorUserId: normalizedActorUserId,
    mutationPhase: "optimistic",
    mutationVersion,
  });

  const abortController = new AbortController();
  let requestTimeoutId = null;

  try {
    const body = new FormData();
    body.append("post_id", postId);
    requestTimeoutId = window.setTimeout(() => {
      abortController.abort();
    }, ORDINARY_POST_LIKE_TIMEOUT_MS);

    const response = await protectedApiFetch(
      `${API_BASE}/posts/like`,
      {
        method: "POST",
        body,
        signal: abortController.signal,
      }
    );
    if (!response.ok) {
      const responseError = new Error(`Post like failed with ${response.status}`);
      responseError.status = response.status;
      throw responseError;
    }

    const data = await response.json();
    if (typeof data?.liked !== "boolean" || !Number.isFinite(Number(data?.likesCount))) {
      throw new Error("Post like response was invalid");
    }

    dispatchPostLikeUpdate({
      postId,
      liked: data.liked,
      likesCount: data.likesCount,
      actorUserId: normalizedActorUserId,
      mutationPhase: "authoritative",
      mutationVersion,
    });
    if (typeof onSuccess === "function") {
      try {
        onSuccess(data);
      } catch (callbackError) {
        console.error("Post like success callback failed:", callbackError);
      }
    }
    return true;
  } catch (error) {
    dispatchPostLikeUpdate({
      postId,
      ...previousState,
      actorUserId: normalizedActorUserId,
      mutationPhase: "rollback",
      mutationVersion,
    });
    const visibleError =
      error?.status === 401
        ? "Your session has expired. Please sign in again."
        : error?.status === 403
          ? "You do not have permission to Like this post."
          : errorMessage || "Could not update the like. Please try again.";
    setPostLikeError(postId, visibleError);
    console.error("Like failed:", error);
    return false;
  } finally {
    if (requestTimeoutId != null) {
      window.clearTimeout(requestTimeoutId);
    }
    inFlightPostIds.delete(postId);
    emitCoordinatorChange();
  }
}

export default function useOrdinaryPostLikeMutation() {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const togglePostLike = useCallback((options) => mutateOrdinaryPostLike(options), []);

  return {
    togglePostLike,
    isPostLikePending: (postId) => inFlightPostIds.has(String(postId || "")),
    latestPostLikeError: getLatestPostLikeError(),
    dismissPostLikeError: clearPostLikeError,
  };
}

export function resetOrdinaryPostLikeMutationForTests() {
  inFlightPostIds.clear();
  clearAllOrdinaryPostLikeErrors();
  revision = 0;
  nextMutationVersion = 0;
  lastMutationTimestamp = 0;
}
